import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DateRange } from '@angular/material/datepicker';
import { MatSelectChange } from '@angular/material/select';
import moment from 'moment';
import { Subject, combineLatest } from 'rxjs';
import { debounceTime, filter, map, take, takeUntil } from 'rxjs/operators';

import { TimeResolution } from '@vi-iot/client';

import { MatTooltip } from '@angular/material/tooltip';
import { TrendingResolution, TrendingStore } from '@store';
import { matTooltipSetter } from 'src/app/shared/functions/mat-tooltip-setter/mat-tooltip-setter';
import { DateRangeForm, MomentDateRangeForm } from '../../../../shared/common/form-types';

@Component({
  selector: 'app-trending-date-range',
  templateUrl: './trending-date-range.component.html',
  styleUrls: ['./trending-date-range.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrendingDateRangeComponent implements OnDestroy, OnInit {
  readonly initStartDate = moment().subtract(6, 'days').startOf('day');
  readonly initEndDate = moment().second(0).millisecond(0);
  readonly minRangepickerDate = moment().subtract(60, 'd').startOf('day');
  readonly maxRangepickerDate = this.initEndDate;
  readonly maxDateRangeForMinuteResolution = 14;
  @ViewChildren(MatTooltip)
  set matTooltip(queryList: QueryList<MatTooltip>) {
    matTooltipSetter(queryList);
  }
  resolutions: { value: TimeResolution; view: string }[] = [
    {
      value: TrendingResolution.Day,
      view: '+INSTALLATIONS.INSTALLATION.TRENDING.CHARTS.1_DAY',
    },
    {
      value: TrendingResolution.Hour,
      view: '+INSTALLATIONS.INSTALLATION.TRENDING.CHARTS.1_HOUR',
    },
    {
      value: TrendingResolution.FiveMinutes,
      view: '+INSTALLATIONS.INSTALLATION.TRENDING.CHARTS.5_MINUTES',
    },
    {
      value: TrendingResolution.Minute,
      view: '+INSTALLATIONS.INSTALLATION.TRENDING.CHARTS.1_MINUTE',
    },
  ];
  dateRangeForm = new FormGroup<MomentDateRangeForm | DateRangeForm>({
    start: new FormControl(this.initStartDate, [Validators.required]),
    end: new FormControl(moment(), [Validators.required]),
  });
  selectedResolution: TimeResolution;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(protected trendingStore: TrendingStore, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.trendingStore.resolution$.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((value) => {
      this.selectedResolution = value;
      this.cdr.markForCheck();
    });

    this.trendingStore.dateRange$
      .pipe(
        filter(
          (newDateRange) =>
            newDateRange &&
            (!moment(newDateRange.start).isSame(this.dateRangeForm.value.start, 'second') ||
              !moment(newDateRange.end).isSame(this.dateRangeForm.value.end, 'second'))
        ),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe((value) => {
        this.dateRangeForm.setValue(value, { emitEvent: false, onlySelf: true });
      });

    combineLatest([this.dateRangeForm.valueChanges, this.trendingStore.dateRange$])
      .pipe(
        debounceTime(300),
        filter(
          ([newDateRange, dateRange]) =>
            newDateRange.start &&
            newDateRange.end &&
            (!moment(dateRange.start).isSame(newDateRange.start, 'second') ||
              !moment(dateRange.end).isSame(newDateRange.end, 'second'))
        ),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe(([newDateRange]) => {
        this.setDateRange(newDateRange as DateRange<moment.Moment>);
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  resolutionChange(resolution: MatSelectChange): void {
    if (resolution.value === TrendingResolution.Minute) {
      this.trendingStore.dateRange$
        .pipe(
          take(1),
          map((dateRange) => new DateRange(moment(dateRange.start), moment(dateRange.end))),
          filter(
            (dateRange) => this.getRangeLength(dateRange) > this.maxDateRangeForMinuteResolution
          ),
          map(
            (dateRange) =>
              new DateRange(
                moment(dateRange.end).subtract(14, 'days').startOf('day'),
                moment(dateRange.end)
              )
          )
        )
        .subscribe((dateRange) => this.setDateRange(dateRange));
    }
    this.trendingStore.setResolution(resolution.value);
  }

  resetDateRange(): void {
    this.setDateRange(new DateRange(this.initStartDate, this.initEndDate));
  }

  setDateRange(dateRange: DateRange<moment.Moment>): void {
    let start = moment(dateRange.start).startOf('day');
    if (
      this.selectedResolution === TrendingResolution.Minute &&
      this.getRangeLength(dateRange) > this.maxDateRangeForMinuteResolution
    ) {
      start = moment(dateRange.end).subtract(14, 'days').startOf('day');
    }

    const end = moment
      .min(moment(dateRange.end).endOf('day'), moment(this.maxRangepickerDate))
      .second(0)
      .millisecond(0);

    this.trendingStore.setDateRange(new DateRange(start.toDate(), end.toDate()));
  }

  private getRangeLength({ start, end }: DateRange<moment.Moment>): number {
    return moment(end).diff(start, 'day');
  }
}
